<script lang="ts" setup>
import type { PatchOrderDto } from "@/api/orders/dto/order.dto";
import type { TripMonitorOrder, TripMonitorOrderNotes, TripMonitorOrderStop } from "@/api/trips/dto/trip-monitor";
import { OrderStopTypeMap } from "@/constants/order";
import { computed, ref, watch } from "vue";
import OrderLink from "@/components/links/OrderLink.vue";
import RouteStopIcon from "@/components/icons/RouteStopIcon.vue";
import { useError } from "@/composables/useError";
import { useI18n } from "vue-i18n";
import { API } from "@/api/api";
import OrderField from "@/components/inputs/OrderField.vue";

const api = new API();
const { showAPIError } = useError();
const { t } = useI18n();

const MENU_WIDTH = 1248;
const MENU_OFFSET: [number, number] = [-200, 400];

const props = defineProps<{
  orders: TripMonitorOrder[];
}>();

const generalNotes = ref<Record<string, string>>({});
const stopNotes = ref<Record<string, string>>({});
const isSaving = ref(false);
const isOpen = ref(false);

const hasNotesChanged = computed(() => {
  return props.orders.some((order) => {
    if (order.generalNote !== generalNotes.value[order.id]) {
      return true;
    }

    return order.orderStops.some((stop) => stop.note !== stopNotes.value[stop.id]);
  });
});

function resetDrafts() {
  const general: Record<string, string> = {};
  const stops: Record<string, string> = {};

  for (const order of props.orders) {
    general[order.id] = order.generalNote;

    for (const stop of order.orderStops) {
      stops[stop.id] = stop.note;
    }
  }

  generalNotes.value = general;
  stopNotes.value = stops;
}

function buildOrderPatch(order: TripMonitorOrder): PatchOrderDto | null {
  let changed = false;

  const orderStops = order.orderStops.map((stop) => {
    if (stop.note !== stopNotes.value[stop.id]) {
      changed = true;
      return { note: stopNotes.value[stop.id] };
    }

    return {};
  });

  const data: PatchOrderDto = { orderStops };

  if (order.generalNote !== generalNotes.value[order.id]) {
    changed = true;
    data.generalNote = generalNotes.value[order.id];
  }

  return changed ? data : null;
}

async function save() {
  if (!hasNotesChanged.value || isSaving.value) {
    return;
  }

  isSaving.value = true;

  try {
    const patches = props.orders
      .map((order) => ({ order, data: buildOrderPatch(order) }))
      .filter((entry): entry is { order: TripMonitorOrder; data: PatchOrderDto } => entry.data !== null);

    const responses = await Promise.all(patches.map(({ order, data }) => api.orders.patchOrder(order.id, data)));

    const failed = responses.find((response) => response.errorData);

    if (failed) {
      showAPIError(failed, t("trip.update-note-failed"));
    }
  } finally {
    isSaving.value = false;
  }
}

function getOrderStopName(orderStop: TripMonitorOrderStop, index: number) {
  return `${OrderStopTypeMap[orderStop.type]}#${index + 1}`;
}

const orderEntries = computed<TripMonitorOrderNotes[]>(() => {
  return props.orders.map((order) => {
    return {
      id: order.id,
      number: order.number,
      generalNote: order.generalNote,
      notes: order.orderStops,
    };
  });
});

const hasNotes = computed(() => {
  return props.orders.some((order) => {
    const hasGeneralNote = Boolean(order.generalNote);
    const orderStopsNote = order.orderStops.some((orderStop) => {
      return Boolean(orderStop.note);
    });

    return hasGeneralNote || orderStopsNote;
  });
});

watch(() => props.orders, resetDrafts, { immediate: true });
</script>

<template>
  <v-menu v-model="isOpen" :close-on-content-click="false" :offset="MENU_OFFSET" :width="MENU_WIDTH">
    <template #activator="{ props: menuProps }">
      <v-btn
        :class="hasNotes ? 'bg-indigo-lighten-1 text-white' : 'text-primary bg-transparent'"
        class="ml-5 font-weight-600 font-size-11"
        data-qa="notes-button"
        flat
        prepend-icon="mdi-format-quote-close"
        size="x-small"
        v-bind="menuProps"
        variant="outlined"
      >
        {{ t("trip.notes") }}
      </v-btn>
    </template>

    <v-card class="elevation-12 border position-relative">
      <v-btn
        class="position-absolute"
        density="comfortable"
        icon="mdi-close"
        style="top: 8px; right: 8px"
        variant="text"
        @click="isOpen = false"
      />

      <div class="py-5 px-6">
        <div v-for="(order, orderIndex) of orderEntries" :key="order.id" :class="{ 'mt-6': orderIndex !== 0 }">
          <order-link :order-id="order.id" :order-number="order.number" />

          <order-field v-model="generalNotes[order.id]" class="mt-3">
            <template #title>
              <span class="font-size-14 text-grey-darken-3">{{ t("trip.general-note") }}</span>
            </template>
          </order-field>

          <div class="mt-3">
            <div
              v-for="(orderStop, index) of order.notes"
              :key="orderStop.id"
              :class="{
                'mt-6': index !== 0,
              }"
            >
              <order-field v-model="stopNotes[orderStop.id]" class="mt-3">
                <template #title>
                  <div class="d-flex align-center col-gap-2">
                    <route-stop-icon :type="orderStop.type" />
                    <span class="font-size-14 text-grey-darken-3">
                      {{ t("trip.stop-note-label", { name: getOrderStopName(orderStop, index) }) }}
                    </span>
                  </div>
                </template>
              </order-field>
            </div>
          </div>
        </div>
      </div>

      <v-card-actions class="d-flex justify-end pa-4">
        <v-btn :disabled="!hasNotesChanged" :loading="isSaving" color="primary" variant="flat" @click="save">{{
          t("general.save")
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<style lang="scss" scoped></style>
